export type ScrollDirection = 'horizontal' | 'vertical';

export function getPrimaryDirection(event: WheelEvent): ScrollDirection {
  return Math.abs(event.deltaX) > Math.abs(event.deltaY) ? 'horizontal' : 'vertical';
}

export function findScrollableContainerWithRoom(
  element: Element | null,
  contentDocument: Document,
  direction: ScrollDirection,
  event: WheelEvent
): Element | null {
  if (!element || !(element instanceof Element)) {
    return hasScrollRoom(contentDocument.documentElement, direction, event)
      ? contentDocument.documentElement
      : null;
  }

  let current: Element | null = element;

  while (current && current instanceof Element) {
    const style = window.getComputedStyle(current);
    const overflowY = style.overflowY;
    const overflowX = style.overflowX;

    const isVerticallyScrollable =
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight;

    const isHorizontallyScrollable =
      (overflowX === 'auto' || overflowX === 'scroll') &&
      current.scrollWidth > current.clientWidth;

    if (
      (direction === 'vertical' && isVerticallyScrollable) ||
      (direction === 'horizontal' && isHorizontallyScrollable)
    ) {
      const canScrollInDirection = hasScrollRoom(current, direction, event);
      if (canScrollInDirection) {
        return current;
      }
    }

    if (current === contentDocument.documentElement || current === contentDocument.body) {
      return hasScrollRoom(contentDocument.documentElement, direction, event)
        ? contentDocument.documentElement
        : null;
    }

    current = current.parentElement;
  }

  return null;
}

export function hasScrollRoom(
  element: Element,
  direction: ScrollDirection,
  event: WheelEvent
): boolean {
  if (direction === 'vertical') {
    const scrollTop = element.scrollTop;
    const maxScrollTop = element.scrollHeight - element.clientHeight;
    const scrollingDown = event.deltaY > 0;
    const scrollingUp = event.deltaY < 0;
    const atTop = scrollTop <= 0;
    const atBottom = scrollTop >= maxScrollTop;

    return (scrollingDown && !atBottom) || (scrollingUp && !atTop);
  }

  const scrollLeft = element.scrollLeft;
  const maxScrollLeft = element.scrollWidth - element.clientWidth;
  const scrollingRight = event.deltaX > 0;
  const scrollingLeft = event.deltaX < 0;
  const atLeft = scrollLeft <= 0;
  const atRight = scrollLeft >= maxScrollLeft;

  return (scrollingRight && !atRight) || (scrollingLeft && !atLeft);
}
